import { useMutation } from '@apollo/client/react';
import { useRef, useState } from 'react';
import type { Icon } from 'react-bootstrap-icons';
import {
  Bell,
  BellSlash,
  Calendar2Week,
  ChatSquareText,
  Envelope,
  Mortarboard,
  People,
  Phone,
  PlayCircle,
  RocketTakeoff,
  Telephone,
  Tools,
} from 'react-bootstrap-icons';

import { PAGE_TITLES } from '@repo/common';
import {
  DataTestIds,
  NotificationCategory,
  NotificationChannel,
  UpdateNotificationPreferencesDocument,
} from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSText } from '@repo/sds/SDSText';
import { SDSToggleButton } from '@repo/sds/SDSToggleButton';
import { toast } from '@repo/sds/utils/toast';
import { NotificationBlock } from '@repo/ui/components/elements/NotificationBlock/NotificationBlock';
import { NotificationOptionItem } from '@repo/ui/components/elements/NotificationOptionItem/NotificationOptionItem';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';

import './UserNotifications.scss';

const DEFAULT_NOTIFICATION_OPTIONS = {
  popupDesktop: false,
  chatNotifications: false,
  updateNotifications: false,
};

const DEFAULT_CATEGORY_PREFERENCES = {
  announcements_courses: false,
  announcements_live_workshops: false,
  announcements_deals_discounts: false,
  announcements_tools_templates: false,
  announcements_live_calls: false,
  mentions_feed: false,
  mentions_scenarios: false,
  sections_alerts: false,
  sections_scenarios: false,
  sections_calendar: false,
};

type NotificationOptions = typeof DEFAULT_NOTIFICATION_OPTIONS;
type CategoryPreferences = typeof DEFAULT_CATEGORY_PREFERENCES;
type NotificationOptionKey = keyof NotificationOptions;
type CategoryPreferenceKey = keyof CategoryPreferences;

interface CategoryOptionDefinition {
  key: CategoryPreferenceKey;
  label: string;
  icon: Icon;
  dataTestId: string;
  labelDataTestId?: string;
}

interface CategorySectionDefinition {
  title: string;
  titleDataTestId: string;
  options: readonly CategoryOptionDefinition[];
}

interface ChannelOptionDefinition {
  key: NotificationOptionKey;
  value: string;
  label: string;
  icon: Icon;
  dataTestId: string;
}

const CATEGORY_SECTIONS: readonly CategorySectionDefinition[] = [
  {
    title: 'Announcements and Updates',
    titleDataTestId: DataTestIds.NOTIFICATIONS.SECTION_ANNOUNCEMENTS,
    options: [
      {
        key: 'announcements_courses',
        label: 'Courses',
        icon: Mortarboard,
        dataTestId: DataTestIds.NOTIFICATIONS.ANNOUNCEMENTS_COURSES,
      },
      {
        key: 'announcements_live_workshops',
        label: 'Live Workshops',
        icon: PlayCircle,
        dataTestId: DataTestIds.NOTIFICATIONS.ANNOUNCEMENTS_LIVE_WORKSHOPS,
      },
      {
        key: 'announcements_deals_discounts',
        label: 'Deals & Discounts',
        icon: RocketTakeoff,
        dataTestId: DataTestIds.NOTIFICATIONS.ANNOUNCEMENTS_DEALS_DISCOUNTS,
        labelDataTestId: DataTestIds.NOTIFICATIONS.LABEL_DEALS_DISCOUNTS,
      },
      {
        key: 'announcements_tools_templates',
        label: 'Tools & Templates',
        icon: Tools,
        dataTestId: DataTestIds.NOTIFICATIONS.ANNOUNCEMENTS_TOOLS_TEMPLATES,
        labelDataTestId: DataTestIds.NOTIFICATIONS.LABEL_TOOLS_TEMPLATES,
      },
      {
        key: 'announcements_live_calls',
        label: '1-1 Live Calls',
        icon: Telephone,
        dataTestId: DataTestIds.NOTIFICATIONS.ANNOUNCEMENTS_LIVE_CALLS,
        labelDataTestId: DataTestIds.NOTIFICATIONS.LABEL_LIVE_CALLS,
      },
    ],
  },
  {
    title: 'Comment Mentions',
    titleDataTestId: DataTestIds.NOTIFICATIONS.SECTION_MENTIONS,
    options: [
      {
        key: 'mentions_feed',
        label: 'Community Feed',
        icon: People,
        dataTestId: DataTestIds.NOTIFICATIONS.MENTIONS_FEED,
        labelDataTestId: DataTestIds.NOTIFICATIONS.LABEL_COMMUNITY_FEED,
      },
      {
        key: 'mentions_scenarios',
        label: 'Scenarios',
        icon: ChatSquareText,
        dataTestId: DataTestIds.NOTIFICATIONS.MENTIONS_SCENARIOS,
      },
    ],
  },
  {
    title: 'Sections',
    titleDataTestId: DataTestIds.NOTIFICATIONS.SECTION_SECTIONS,
    options: [
      {
        key: 'sections_alerts',
        label: 'Alerts',
        icon: Bell,
        dataTestId: DataTestIds.NOTIFICATIONS.SECTIONS_ALERTS,
      },
      {
        key: 'sections_scenarios',
        label: 'Scenarios',
        icon: ChatSquareText,
        dataTestId: DataTestIds.NOTIFICATIONS.SECTIONS_SCENARIOS,
      },
      {
        key: 'sections_calendar',
        label: 'Calendar',
        icon: Calendar2Week,
        dataTestId: DataTestIds.NOTIFICATIONS.SECTIONS_CALENDAR,
      },
    ],
  },
];

const CHANNEL_OPTIONS: readonly ChannelOptionDefinition[] = [
  {
    key: 'popupDesktop',
    value: 'push-notifications',
    label: 'Push Notifications',
    icon: Bell,
    dataTestId: DataTestIds.NOTIFICATIONS.CHANNEL_PUSH,
  },
  {
    key: 'chatNotifications',
    value: 'in-app-message',
    label: 'In-app Message',
    icon: Phone,
    dataTestId: DataTestIds.NOTIFICATIONS.CHANNEL_IN_APP,
  },
  {
    key: 'updateNotifications',
    value: 'email',
    label: 'Email',
    icon: Envelope,
    dataTestId: DataTestIds.NOTIFICATIONS.CHANNEL_EMAIL,
  },
];

const NOTIFICATION_CATEGORIES: Record<CategoryPreferenceKey, NotificationCategory> = {
  announcements_courses: NotificationCategory.ANNOUNCEMENTS_COURSES,
  announcements_live_workshops: NotificationCategory.ANNOUNCEMENTS_LIVE_WORKSHOPS,
  announcements_deals_discounts: NotificationCategory.ANNOUNCEMENTS_DEALS_DISCOUNTS,
  announcements_tools_templates: NotificationCategory.ANNOUNCEMENTS_TOOLS_TEMPLATES,
  announcements_live_calls: NotificationCategory.ANNOUNCEMENTS_LIVE_CALLS,
  mentions_feed: NotificationCategory.MENTIONS_FEED,
  mentions_scenarios: NotificationCategory.MENTIONS_SCENARIOS,
  sections_alerts: NotificationCategory.SECTIONS_ALERTS,
  sections_scenarios: NotificationCategory.SECTIONS_SCENARIOS,
  sections_calendar: NotificationCategory.SECTIONS_CALENDAR,
};

const NOTIFICATION_CHANNELS: Record<NotificationOptionKey, NotificationChannel> = {
  popupDesktop: NotificationChannel.PUSH,
  chatNotifications: NotificationChannel.IN_APP,
  updateNotifications: NotificationChannel.EMAIL,
};

function parseCategoryPreferences(value: string | undefined): CategoryPreferences {
  if (!value) return DEFAULT_CATEGORY_PREFERENCES;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_CATEGORY_PREFERENCES;
    return { ...DEFAULT_CATEGORY_PREFERENCES, ...parsed };
  } catch {
    return DEFAULT_CATEGORY_PREFERENCES;
  }
}

function NotificationCategorySections({
  preferences,
  disabled,
  onChange,
}: {
  preferences: CategoryPreferences;
  disabled: boolean;
  onChange(key: CategoryPreferenceKey, checked: boolean): void;
}) {
  return CATEGORY_SECTIONS.map(section => (
    <NotificationBlock key={section.title} title={section.title} titleDataTestId={section.titleDataTestId}>
      <SDSFlexbox direction="column" gap="no-design-8">
        {section.options.map(option => {
          const OptionIcon = option.icon;
          return (
            <NotificationOptionItem
              key={option.key}
              label={option.label}
              icon={<OptionIcon />}
              checked={!disabled && preferences[option.key]}
              disabled={disabled}
              dataTestId={option.dataTestId}
              labelDataTestId={option.labelDataTestId}
              onChange={() => onChange(option.key, !preferences[option.key])}
            />
          );
        })}
      </SDSFlexbox>
    </NotificationBlock>
  ));
}

function NotificationChannelOptions({
  options,
  disabled,
  onChange,
}: {
  options: NotificationOptions;
  disabled: boolean;
  onChange(key: NotificationOptionKey, checked: boolean): void;
}) {
  return (
    <NotificationBlock title="Display" collapsible={false} titleDataTestId={DataTestIds.NOTIFICATIONS.SECTION_DISPLAY}>
      <SDSFlexbox direction="row" gap="button-medium" alignItems="center" justifyContent="space-between" fullWidth>
        {CHANNEL_OPTIONS.map(option => {
          const OptionIcon = option.icon;
          return (
            <SDSToggleButton
              key={option.key}
              value={option.value}
              pressed={!disabled && options[option.key]}
              onPressedChange={pressed => onChange(option.key, pressed)}
              isDisabled={disabled}
              size="medium"
              ariaLabel={option.label}
              className="UserNotifications__toggleButton"
              dataTestId={option.dataTestId}
            >
              <SDSFlexbox direction="row" gap="text-small" alignItems="center" justifyContent="center">
                <SDSIcon icon={<OptionIcon />} size="small" className="UserNotifications__icon" color="text-primary" />
                <SDSText type="body-medium" text={option.label} />
              </SDSFlexbox>
            </SDSToggleButton>
          );
        })}
      </SDSFlexbox>
    </NotificationBlock>
  );
}

export function NotificationsPage() {
  const { currentUser, saving, reloadCurrentUser } = useCurrentUser();
  const [updateNotificationPrefs] = useMutation(UpdateNotificationPreferencesDocument);
  const preferences = currentUser?.notificationPreferences;
  const preferenceKey = JSON.stringify(preferences ?? null);
  const serverOptions: NotificationOptions = {
    popupDesktop: Boolean(preferences?.channelPreferences?.push?.enabled),
    chatNotifications: Boolean(preferences?.channelPreferences?.inApp?.enabled),
    // An email Unsubscribe from all shows as Email off; turning it on takes the Unsubscribe back.
    updateNotifications:
      Boolean(preferences?.channelPreferences?.email?.enabled) && !preferences?.absoluteSettings?.unsubscribedFromAll,
  };
  const serverCategoryPreferences = parseCategoryPreferences(preferences?.categoryPreferences);
  const [optionsSelection, setOptionsSelection] = useState<{
    sourceKey: string;
    value: NotificationOptions;
  } | null>(null);
  const [categorySelection, setCategorySelection] = useState<{
    sourceKey: string;
    value: CategoryPreferences;
  } | null>(null);
  const categoryIntentRef = useRef<Partial<CategoryPreferences>>({});
  const optionIntentRef = useRef<Partial<NotificationOptions>>({});
  const writeStateRef = useRef<{
    queues: Map<string, Promise<void>>;
    versions: Map<string, number>;
    refreshQueue: Promise<void>;
  } | null>(null);
  const pendingWriteCountRef = useRef(0);
  const writeGenerationRef = useRef(0);
  const preparedOptions = optionsSelection?.sourceKey === preferenceKey ? optionsSelection.value : serverOptions;
  const categoryPreferences =
    categorySelection?.sourceKey === preferenceKey ? categorySelection.value : serverCategoryPreferences;

  const getWriteState = () => {
    const existing = writeStateRef.current;
    if (existing) return existing;
    const initialized = {
      queues: new Map<string, Promise<void>>(),
      versions: new Map<string, number>(),
      refreshQueue: Promise.resolve(),
    };
    writeStateRef.current = initialized;
    return initialized;
  };

  const refreshAfterQueueDrain = (generation: number) => {
    const writeState = getWriteState();
    const refresh = writeState.refreshQueue.then(async () => {
      await reloadCurrentUser();
    });
    writeState.refreshQueue = refresh.catch(() => undefined);
    void refresh
      .then(() => {
        if (pendingWriteCountRef.current !== 0 || writeGenerationRef.current !== generation) return;
        categoryIntentRef.current = {};
        optionIntentRef.current = {};
      })
      .catch(() => {
        toast.error('Failed to refresh notification settings.');
      });
  };

  const enqueuePreferenceWrite = (
    writeKey: string,
    execute: () => Promise<unknown>,
    rollbackLatestIntent: () => void,
  ) => {
    const writeState = getWriteState();
    const version = (writeState.versions.get(writeKey) ?? 0) + 1;
    writeState.versions.set(writeKey, version);
    pendingWriteCountRef.current += 1;
    writeGenerationRef.current += 1;

    const previousWrite = writeState.queues.get(writeKey) ?? Promise.resolve();
    const write = previousWrite.then(async () => {
      try {
        await execute();
      } catch {
        toast.error('Failed to update notification settings.');
        if (writeState.versions.get(writeKey) === version) rollbackLatestIntent();
      }
    });
    writeState.queues.set(writeKey, write);

    void write.then(() => {
      pendingWriteCountRef.current -= 1;
      if (writeState.queues.get(writeKey) === write) writeState.queues.delete(writeKey);
      if (pendingWriteCountRef.current === 0) refreshAfterQueueDrain(writeGenerationRef.current);
    });
  };

  const handleCategoryChange = (categoryKey: CategoryPreferenceKey, checked: boolean) => {
    if (!currentUser) return;
    if (saving) return;

    const previousValue = categoryIntentRef.current[categoryKey] ?? categoryPreferences[categoryKey];
    categoryIntentRef.current[categoryKey] = checked;
    setCategorySelection(current => ({
      sourceKey: preferenceKey,
      value: {
        ...(current?.sourceKey === preferenceKey ? current.value : categoryPreferences),
        [categoryKey]: checked,
      },
    }));

    enqueuePreferenceWrite(
      `category:${categoryKey}`,
      () =>
        updateNotificationPrefs({
          variables: {
            input: {
              category: NOTIFICATION_CATEGORIES[categoryKey],
              enabled: checked,
            },
          },
        }),
      () => {
        categoryIntentRef.current[categoryKey] = previousValue;
        setCategorySelection(current => {
          if (current?.sourceKey !== preferenceKey || current.value[categoryKey] !== checked) return current;
          return { ...current, value: { ...current.value, [categoryKey]: previousValue } };
        });
      },
    );
  };

  const handleToggleChange = (settingKey: NotificationOptionKey, checked?: boolean) => {
    if (!currentUser) return;
    if (saving) return;

    const previousValue = optionIntentRef.current[settingKey] ?? preparedOptions[settingKey];
    const newValue = typeof checked === 'boolean' ? checked : !previousValue;
    optionIntentRef.current[settingKey] = newValue;
    setOptionsSelection(current => ({
      sourceKey: preferenceKey,
      value: {
        ...(current?.sourceKey === preferenceKey ? current.value : preparedOptions),
        [settingKey]: newValue,
      },
    }));

    enqueuePreferenceWrite(
      `channel:${settingKey}`,
      () =>
        updateNotificationPrefs({
          variables: {
            input: {
              channels: [NOTIFICATION_CHANNELS[settingKey]],
              enabled: newValue,
            },
          },
        }),
      () => {
        optionIntentRef.current[settingKey] = previousValue;
        setOptionsSelection(current => {
          if (current?.sourceKey !== preferenceKey || current.value[settingKey] !== newValue) return current;
          return { ...current, value: { ...current.value, [settingKey]: previousValue } };
        });
      },
    );
  };

  const onHandleDisableAll = async () => {
    if (!currentUser) return;
    if (saving) return;

    const currentDisableAll = currentUser.notificationPreferences?.disableAllNotification ?? false;
    try {
      await updateNotificationPrefs({
        variables: {
          input: {
            disableAllNotification: !currentDisableAll,
          },
        },
      });
      await reloadCurrentUser();
    } catch {
      toast.error('Failed to update notification settings.');
    }
  };

  // Read the disableAllNotification field from backend
  const allDisabled = currentUser?.notificationPreferences?.disableAllNotification ?? false;

  return (
    <div className="UserNotifications" data-testid={DataTestIds.NOTIFICATIONS.PAGE}>
      <SDSFlexbox direction="column" className="UserNotifications__wrapper" gap="card-large">
        <SDSFlexbox className="UserNotifications__titleContent" direction="column" gap="text-medium">
          <SDSText
            text={PAGE_TITLES.SETTINGS.NOTIFICATIONS}
            type="h5"
            dataTestId={DataTestIds.ADMIN.SIDEBAR.PAGE_TITLE}
          />
          <SDSText text="Manage your notifications here" type="body-medium" color="muted" />
        </SDSFlexbox>
        <SDSFlexbox direction="column" gap="no-design-16">
          <NotificationCategorySections
            preferences={categoryPreferences}
            disabled={allDisabled}
            onChange={handleCategoryChange}
          />
          <NotificationChannelOptions options={preparedOptions} disabled={allDisabled} onChange={handleToggleChange} />
          <NotificationOptionItem
            label="Disable all notifications"
            icon={<BellSlash />}
            checked={allDisabled}
            onChange={onHandleDisableAll}
            dataTestId={DataTestIds.NOTIFICATIONS.DISABLE_ALL}
          />
        </SDSFlexbox>
      </SDSFlexbox>
    </div>
  );
}
